import { request } from './client.js';
import type {
  CrossClientDetectionPage,
  DetectionDetail,
  DetectionListParams,
  DetectionPage,
  DetectionPayload,
  EnrichmentResponse,
} from './types.js';

/**
 * Every visible Client's stream, merged by time (task 66). Same filters as
 * `listDetections`; the cursor is its own kind and only round-trips here.
 */
export function listAllDetections(
  params: DetectionListParams,
  idToken: string,
): Promise<CrossClientDetectionPage> {
  return request<CrossClientDetectionPage>('GET', '/detections', idToken, {
    query: { ...params },
  });
}

export function listDetections(
  clientTenantId: string,
  params: DetectionListParams,
  idToken: string,
): Promise<DetectionPage> {
  return request<DetectionPage>(
    'GET',
    `/tenants/${encodeURIComponent(clientTenantId)}/detections`,
    idToken,
    { query: { ...params } },
  );
}

/** `detectionId` is `<source>:<provider_detection_id>`; the colon is encoded here. */
export function getDetection(
  clientTenantId: string,
  detectionId: string,
  idToken: string,
): Promise<DetectionDetail> {
  return request<DetectionDetail>(
    'GET',
    `/tenants/${encodeURIComponent(clientTenantId)}/detections/${encodeURIComponent(detectionId)}`,
    idToken,
  );
}

/**
 * The provider's full payload, read from the lake (task 67). Call it on
 * click, never on page load: it is a separate S3 read per request.
 */
export function getDetectionPayload(
  clientTenantId: string,
  detectionId: string,
  idToken: string,
): Promise<DetectionPayload> {
  return request<DetectionPayload>(
    'GET',
    `/tenants/${encodeURIComponent(clientTenantId)}/detections/${encodeURIComponent(detectionId)}/payload`,
    idToken,
  );
}

/**
 * Ask for first-open enrichment (Story 6). 202 `pending` when the API claimed
 * the detection and invoked the enrich Lambda; 200 with the current state
 * when someone else already did, or it is already ready. Poll the detail.
 */
export function requestEnrichment(
  clientTenantId: string,
  detectionId: string,
  idToken: string,
): Promise<EnrichmentResponse> {
  return request<EnrichmentResponse>(
    'POST',
    `/tenants/${encodeURIComponent(clientTenantId)}/detections/${encodeURIComponent(detectionId)}/enrich`,
    idToken,
  );
}
